import React, {Fragment, ReactElement, useMemo} from 'react';
import {FormattedMessage} from 'react-intl';

// Components

import AddressItem from '../../summary/AddressItem';
import SummaryItem from '../SummaryItem';
import ResidencySummary from '../ResidencySummary';

// Utils

import {checkIfAddressesEqual} from '@utils/miscellaneous/checkIfAddressesEqual';
import {formatSummaryAddressSecondLine} from '@utils/miscellaneous/formatSummaryAddressSecondLine';

// Constants

// Types

import {Address, IsState, ResidencyInfo} from '@components/ssap/SsapTypes';

interface IAddressesInfo {
    addressVisibilityDueToGeneralDelivery: { [key: string]: boolean },
    applicantGuid: string,
    applyingForCoverageIndicator: boolean,
    customAddressItemProps?: Array<string> | null,
    hiddenSummaryItems?: Array<string>,
    addressConfidentialityParticipant: boolean,
    showAddressConfidentiality: boolean,
    additionalResidencyEnabled: boolean,
    residencyInfo: ResidencyInfo,
    homeAddress: Address,
    isState: IsState,
    mailingAddress: Address,
    mailingAddressFromPortal?: Address,
    pcpIndex: number
}

const AddressesInfo = (props: IAddressesInfo): ReactElement => {

    const {
        addressVisibilityDueToGeneralDelivery,
        applicantGuid,
        applyingForCoverageIndicator,
        customAddressItemProps,
        hiddenSummaryItems = [],
        addressConfidentialityParticipant,
        showAddressConfidentiality,
        additionalResidencyEnabled,
        residencyInfo,
        homeAddress,
        isState,
        mailingAddress,
        mailingAddressFromPortal,
        pcpIndex
    } = props;

    const areMailingAddressesDifferent: boolean = useMemo(
        () => {
            if (!mailingAddressFromPortal) {
                return false;
            }
            return !checkIfAddressesEqual(mailingAddressFromPortal, mailingAddress);
        },
        [mailingAddressFromPortal, mailingAddress]
    );

    const isAddressDiffHidden = hiddenSummaryItems.includes('addressDiff');
    const isHomeAddressHidden = hiddenSummaryItems.includes('homeAddress');
    const isMailingAddressHidden = hiddenSummaryItems.includes('mailingAddress');

    return (
        <Fragment>
            {
                showAddressConfidentiality && <SummaryItem
                    textCode='ssap.summary.label.addressConfidentialityParticipant'
                    value={!!addressConfidentialityParticipant}
                    desc={[['addressConfidentialityParticipant']]}
                    summaryItemId={'addressConfidentialityParticipant'}
                />
            }
            <AddressItem
                customAddressItemProps={customAddressItemProps}
                isHidden={isHomeAddressHidden}
                textCode='common.home_address'
                {...homeAddress}
                getAddressSecondLine={formatSummaryAddressSecondLine}
                isStreetAddress1Hidden={addressVisibilityDueToGeneralDelivery[applicantGuid]}
                addressDescriptors={
                    [
                        [
                            [
                                `taxHousehold.householdMember[${pcpIndex}].householdContact.homeAddress.streetAddress1`
                            ]
                        ],
                        [
                            [
                                `taxHousehold.householdMember[${pcpIndex}].householdContact.homeAddress.streetAddress2`
                            ]
                        ],
                        [
                            [
                                [
                                    `taxHousehold.householdMember[${pcpIndex}].householdContact.homeAddress.city`,
                                    `taxHousehold.householdMember[${pcpIndex}].householdContact.homeAddress.state`,
                                    `taxHousehold.householdMember[${pcpIndex}].householdContact.homeAddress.postalCode`,
                                    `taxHousehold.householdMember[${pcpIndex}].householdContact.homeAddress.county`
                                ],
                                formatSummaryAddressSecondLine
                            ]
                        ]
                    ]
                }
            />

            <ResidencySummary
                index={pcpIndex}
                additionalResidencyEnabled={additionalResidencyEnabled && applyingForCoverageIndicator}
                residencyInfo={residencyInfo}
            />

            <AddressItem
                customAddressItemProps={customAddressItemProps}
                isHidden={isMailingAddressHidden}
                textCode='common.mailing_address'
                {...mailingAddress}
                getAddressSecondLine={formatSummaryAddressSecondLine}
                addressDescriptors={
                    [
                        [
                            [
                                `taxHousehold.householdMember[${pcpIndex}].householdContact.mailingAddress.streetAddress1`
                            ]
                        ],
                        [
                            [
                                `taxHousehold.householdMember[${pcpIndex}].householdContact.mailingAddress.streetAddress2`
                            ]
                        ],
                        [
                            [
                                [
                                    `taxHousehold.householdMember[${pcpIndex}].householdContact.mailingAddress.city`,
                                    `taxHousehold.householdMember[${pcpIndex}].householdContact.mailingAddress.state`,
                                    `taxHousehold.householdMember[${pcpIndex}].householdContact.mailingAddress.postalCode`,
                                    `taxHousehold.householdMember[${pcpIndex}].householdContact.mailingAddress.county`
                                ],
                                formatSummaryAddressSecondLine
                            ]
                        ]
                    ]
                }
            />

            {
                isState.ID && areMailingAddressesDifferent && !isAddressDiffHidden &&

                (
                    <div className='usa-alert gi-alert gi-alert__left-accent gi-alert__info gi-alert__print_hidden'>
                        <div className='gi-alert__body'>
                            <p className='gi-alert__text'>
                                <FormattedMessage id='ssap.final_review.mailing_addresses_diff_alert'/>
                            </p>
                        </div>
                    </div>
                )
            }
        </Fragment>
    );
};

export default AddressesInfo;